// File: @/lib/analytics/providers/ga4.ts

/**
 * GA4 provider adapter — maps canonical AnalyticsEvents to gtag() calls.
 *
 * Naming rules (why the names below look the way they do):
 *   - GA4 event names allow only letters, digits and underscores (no dots).
 *   - Names must not reuse GA4's automatic / Enhanced Measurement events
 *     (`click`, `scroll`, `user_engagement`, `page_view`, `form_start`, …),
 *     otherwise our events are merged into GA4's own counts.
 *   - Parameters are plain descriptive keys (register the ones you want to
 *     report on as event-scoped custom dimensions in GA4 Admin).
 *
 * GA4_EVENT_NAMES doubles as the routing table: an event without an entry is
 * not sent to GA4 (see core.ts).
 */

import { CURRENT_SITE } from '@/config/sites';

import type { AnalyticsEvent, EventName } from '../types';

type Ga4Params = Readonly<Record<string, string | number>>;

/** GA4 truncates standard-property parameter values beyond 100 characters. */
const GA4_MAX_PARAM_VALUE_LENGTH = 100;

export const GA4_EVENT_NAMES: Readonly<Partial<Record<EventName, string>>> = {
  'engagement.scroll_depth': 'scroll_depth',
  'engagement.qualified_visit': 'qualified_visit',
  'engagement.content_completion': 'content_completion',
  'engagement.read_time': 'read_time',
  'interaction.external_link_click': 'external_link_click',
  'interaction.social_click': 'social_click',
  'interaction.cta_click': 'cta_click',
};

// Accesses window.gtag, typed in src/types/gtag.d.ts
function fireGtag(eventName: string, params: Ga4Params): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
}

function truncate(value: string): string {
  return value.slice(0, GA4_MAX_PARAM_VALUE_LENGTH);
}

/**
 * Sends the event's own fields as GA4 parameters. `site_id` is added to every
 * event so one GA4 property can compare the campaign domains.
 */
function buildGa4Params(event: AnalyticsEvent): Ga4Params {
  const { name: _name, version: _version, ...fields } = event;
  const eventParams = Object.entries(fields)
    .filter((entry): entry is [string, string | number] => entry[1] !== undefined)
    .map(([key, value]) => [key, typeof value === 'string' ? truncate(value) : value] as const);

  return { site_id: CURRENT_SITE.id, ...Object.fromEntries(eventParams) };
}

export function sendToGa4(event: AnalyticsEvent): void {
  const ga4EventName = GA4_EVENT_NAMES[event.name];
  if (!ga4EventName) return;

  fireGtag(ga4EventName, buildGa4Params(event));
}
